import {useCallback} from 'react'
import {Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {type NavigationProp} from '#/lib/routes/types'
import {useConvoQuery} from '#/state/queries/messages/conversation'
import {useSession} from '#/state/session'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {ChevronRight_Stroke2_Corner0_Rounded as ChevronRightIcon} from '#/components/icons/Chevron'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

/**
 * Safety step before an invitation is sent in a Taderok chat: shows exactly
 * WHO will receive it (avatar + name + @handle, with a "View profile" tap)
 * so an invite can never go to the wrong person by mistake.
 */
export function FamilyRecipientConfirmDialog({
  control,
  chatId,
  demoPeer,
  onSend,
}: {
  control: Dialog.DialogControlProps
  /** Real chat — the recipient is resolved from the convo members. */
  chatId?: string
  /** Demo mode — the recipient is the simulated peer (no network). */
  demoPeer?: {did: string; handle: string; displayName: string}
  onSend: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const {currentAccount} = useSession()
  const {data: convo, isLoading} = useConvoQuery({
    convoId: chatId ?? '',
  })

  const recipient = useCallback(() => {
    // Demo mode: the simulated peer is the recipient.
    if (demoPeer) return demoPeer
    if (!convo) return undefined
    const me = currentAccount?.did
    const member = convo.members.find(m => m.did !== me)
    if (!member) return undefined
    return {
      did: member.did,
      handle: member.handle,
      displayName: member.displayName,
    }
  }, [demoPeer, convo, currentAccount])

  const isLoadingRecipient = !demoPeer && isLoading

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Confirm the recipient`)}>
        <View style={[a.gap_lg]}>
          <View style={[a.gap_xs]}>
            <Text style={[a.text_2xl, a.font_bold]}>
              <Trans>Who will receive this?</Trans>
            </Text>
            <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
              <Trans>
                Double-check the person before sending the invitation.
              </Trans>
            </Text>
          </View>

          {isLoadingRecipient ? (
            <Loader size="lg" />
          ) : (
            <RecipientRow
              recipient={recipient()}
              demoPeer={demoPeer}
              onOpenProfile={() => {
                if (recipient()?.handle) {
                  navigation.navigate('Profile', {
                    name: recipient()?.handle ?? '',
                  })
                }
              }}
            />
          )}

          <View style={[a.flex_row, a.gap_sm, a.justify_end]}>
            <Button
              label={_(msg`Cancel`)}
              size="small"
              color="secondary"
              onPress={() => control.close()}>
              <ButtonText>
                <Trans>Cancel</Trans>
              </ButtonText>
            </Button>
            <Button
              label={_(msg`Send invitation`)}
              size="small"
              color="primary"
              disabled={!recipient()}
              onPress={() => control.close(() => onSend())}>
              <ButtonText>
                <Trans>Send invitation</Trans>
              </ButtonText>
            </Button>
          </View>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/**
 * The recipient card. In real mode the WHOLE row is tappable — it opens the
 * recipient's profile so the sender can verify who they are (and that they can
 * receive messages) before tapping Send. Demo peers have no real profile, so
 * the row stays a plain read-only card there.
 */
function RecipientRow({
  recipient,
  demoPeer,
  onOpenProfile,
}: {
  recipient?: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
  }
  demoPeer?: {did: string; handle: string; displayName: string; avatar?: string}
  onOpenProfile: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const name = recipient?.displayName || recipient?.handle || '…'
  const canOpenProfile = recipient != null && !demoPeer

  const face = recipient?.avatar ? (
    <UserAvatar avatar={recipient.avatar} size={48} type="user" />
  ) : (
    <View
      style={[
        {
          width: 48,
          height: 48,
          borderRadius: 24,
          backgroundColor: t.palette.primary_100,
        },
        a.align_center,
        a.justify_center,
      ]}>
      <Text style={[a.text_lg, a.font_bold, {color: t.palette.primary_700}]}>
        {(name[0] || '?').toUpperCase()}
      </Text>
    </View>
  )

  const info = (
    <View style={[a.flex_1, a.gap_2xs]}>
      <Text style={[a.text_md, a.font_bold]} numberOfLines={1}>
        {name}
      </Text>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium]} numberOfLines={1}>
        @{recipient?.handle || ''}
      </Text>
    </View>
  )

  const chevron = canOpenProfile ? (
    <ChevronRightIcon width={16} fill={t.palette.primary_600} />
  ) : null

  const rowStyle = [
    a.flex_row,
    a.align_center,
    a.gap_md,
    a.p_lg,
    a.rounded_md,
    a.border,
    t.atoms.border_contrast_low,
    t.atoms.bg_contrast_25,
  ]

  if (canOpenProfile) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={_(msg`View ${name}'s profile`)}
        accessibilityHint={_(
          msg`Opens the recipient's profile so you can verify who they are before sending`,
        )}
        onPress={onOpenProfile}
        style={({pressed}) => [...rowStyle, pressed && t.atoms.bg_contrast_50]}>
        {face}
        {info}
        {chevron}
      </Pressable>
    )
  }
  return (
    <View style={rowStyle}>
      {face}
      {info}
      {chevron}
    </View>
  )
}
